import { FiCheck } from "react-icons/fi";
import { SECTION_LABELS, type FactFinderSection } from "@/types/fact-finder";
import { useFactFinderConfig } from "@/contexts/FactFinderConfigContext";
import { getActiveSections } from "@/config/fact-finder";

interface FormProgressProps {
  currentSection: FactFinderSection;
  completedSections: string[];
  onSectionClick: (section: FactFinderSection) => void;
}

export function FormProgress({
  currentSection,
  completedSections,
  onSectionClick,
}: FormProgressProps) {
  const config = useFactFinderConfig();
  const sections = getActiveSections(config) as readonly FactFinderSection[];

  // Filter completed sections to only include active sections
  const activeCompletedSections = completedSections.filter((s) =>
    sections.includes(s as FactFinderSection)
  );

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <h3 className="font-medium mb-4">Your Progress</h3>
      <nav className="space-y-2">
        {sections.map((section, index) => {
          const isCompleted = activeCompletedSections.includes(section);
          const isCurrent = section === currentSection;
          const isAccessible = index === 0 || activeCompletedSections.includes(sections[index - 1]) || isCompleted;

          return (
            <button
              key={section}
              onClick={() => isAccessible && onSectionClick(section)}
              disabled={!isAccessible}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                isCurrent
                  ? "bg-primary text-primary-foreground"
                  : isCompleted
                  ? "bg-green-50 text-green-700 hover:bg-green-100"
                  : isAccessible
                  ? "hover:bg-muted text-foreground"
                  : "text-muted-foreground cursor-not-allowed"
              }`}
            >
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                  isCurrent
                    ? "bg-primary-foreground text-primary"
                    : isCompleted
                    ? "bg-green-500 text-white"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {isCompleted ? <FiCheck className="size-3" /> : index + 1}
              </div>
              <span className="truncate">
                {config.advancedMode ? SECTION_LABELS[section] : (
                  section === "applicants" ? "Personal Details" :
                  section === "consent" ? "Privacy & Consent" :
                  SECTION_LABELS[section]
                )}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Progress percentage */}
      <div className="mt-6 pt-4 border-t">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-muted-foreground">Overall Progress</span>
          <span className="font-medium">
            {Math.round((activeCompletedSections.length / sections.length) * 100)}%
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${(activeCompletedSections.length / sections.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
